import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, Info } from "lucide-react";

const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light focus:border-cfhtb-secondary";
const labelCls = "block text-sm font-medium text-cfhtb-heading mb-1.5";

export default function Contact() {
  const { user } = useAuth();
  const [disponible, setDisponible] = useState(null);
  const [sujet, setSujet] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [envoye, setEnvoye] = useState(null);

  useEffect(() => {
    api("/contact/disponible").then((d) => setDisponible(d.configuree)).catch(() => setDisponible(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api("/contact", { method: "POST", body: { sujet, message } });
      setEnvoye(res);
      toast.success("Votre message a été envoyé");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppLayout variant="membre">
      <div data-testid="contact-page" className="max-w-2xl space-y-6 animate-fade-in">
        <div>
          <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Contact</h1>
          <p className="text-sm text-cfhtb-text mt-1">
            Une question, une remarque ? Écrivez à l'équipe CFHTB depuis ce formulaire.
          </p>
        </div>

        {disponible === false && (
          <div data-testid="contact-unavailable" className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
            <Info className="h-5 w-5 shrink-0 mt-0.5" />
            <p>Le formulaire de contact n'est pas encore disponible. Réessayez ultérieurement.</p>
          </div>
        )}

        {envoye ? (
          <div data-testid="contact-success" className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-8 text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-cfhtb-success mx-auto" />
            <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">Message envoyé</h2>
            <p className="text-sm text-cfhtb-text">
              Votre message a bien été transmis à l'équipe CFHTB.
              {envoye.accuse_envoye && (
                <> Un <b>accusé de réception</b> vient de vous être envoyé à <b>{user?.email}</b>.</>
              )}
            </p>
            <button data-testid="contact-new-message-button"
              onClick={() => { setEnvoye(null); setSujet(""); setMessage(""); }}
              className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg transition-colors">
              <Mail className="h-4 w-4" /> Envoyer un autre message
            </button>
          </div>
        ) : disponible && (
          <form onSubmit={submit} className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-5">
            <div>
              <label className={labelCls}>Objet *</label>
              <input data-testid="contact-sujet-input" required maxLength={200} className={inputCls}
                value={sujet} onChange={(e) => setSujet(e.target.value)}
                placeholder="Ex. : Question sur mon adhésion" />
            </div>
            <div>
              <label className={labelCls}>Message *</label>
              <textarea data-testid="contact-message-input" required rows={8} maxLength={5000}
                className={inputCls} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Votre message…" />
              <p className="text-xs text-cfhtb-text/70 mt-1">{message.length}/5000 caractères</p>
            </div>
            <div className="flex items-center justify-between gap-4 pt-1">
              <p className="text-xs text-cfhtb-text/70">
                Envoyé en tant que <b>{user?.prenom} {user?.nom}</b> ({user?.email}). Un accusé de réception vous sera adressé par email.
              </p>
              <button type="submit" data-testid="contact-submit-button" disabled={busy}
                className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-60 shrink-0">
                <Send className="h-4 w-4" /> {busy ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </form>
        )}
      </div>
    </AppLayout>
  );
}
